import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const W = 600;
const H = 380;
const HORIZON_Y = 140;
const PLAYER_Y = H - 65;
const ROAD_TOP_W = 60;
const ROAD_BOTTOM_W = 460;
const FINISH_DIST = 4000;
const MAX_SPEED = 180;

const AI_RIVALS = [
  { name: 'GPT-Raider',  color: '#10a37f', taunt: '"Generating your defeat..."'        },
  { name: 'Gemini Pro',  color: '#4285f4', taunt: '"Riding in parallel universes!"'    },
  { name: 'Copilot',     color: '#0078d4', taunt: '"Auto-completing to 1st place!"'    },
  { name: 'Llama 3',     color: '#7c3aed', taunt: '"Open source, open throttle!"'      },
  { name: 'Mistral',     color: '#f97316', taunt: '"Sacré bleu, get out my way!"'      },
  { name: 'Grok',        color: '#e74c3c', taunt: '"Max truth, zero brakes!"'           },
  { name: 'DeepSeek',    color: '#00b4d8', taunt: '"Searched for mercy. 404."'          },
  { name: 'Claude 3',    color: '#cc785c', taunt: '"Helpful, harmless, and 1st!"'      },
];

const PUNCH_TEXTS = [
  'HALLUCINATED THAT!', 'RATE LIMITED!', '404: DEFENSE NOT FOUND!',
  'CONTEXT WINDOW: CLOSED!', 'GPU MELTED!', 'DEPRECATED!',
  'TOKEN LIMIT EXCEEDED!', 'FINE-TUNED YOUR FACE!', 'MODEL COLLAPSED!',
];

function lerp(a, b, t) { return a + (b - a) * Math.max(0, Math.min(1, t)); }
function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
function rnd(lo, hi) { return lo + Math.random() * (hi - lo); }
function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// Project road-space coords to screen
// laneX: -1 (left) to 1 (right), depth: 0 (horizon) to 1 (player)
function project(laneX, depth, camX) {
  const baseX = lerp(W / 2 + camX, W / 2, depth);
  const hw = lerp(ROAD_TOP_W / 2, ROAD_BOTTOM_W / 2, depth);
  return {
    x: baseX + laneX * hw,
    y: HORIZON_Y + (PLAYER_Y - HORIZON_Y) * depth,
    scale: lerp(0.07, 1.0, Math.pow(depth, 0.75)),
  };
}

function rrect(ctx, x, y, w, h, r) {
  if (ctx.roundRect) {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.rect(x, y, w, h);
  }
}

// ─── Drawing ─────────────────────────────────────────────────────────────────

function drawSky(ctx) {
  const g = ctx.createLinearGradient(0, 0, 0, HORIZON_Y);
  g.addColorStop(0, '#1565a8');
  g.addColorStop(1, '#87ceeb');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, HORIZON_Y);
  // Sun
  ctx.fillStyle = 'rgba(255,255,180,0.75)';
  ctx.beginPath();
  ctx.arc(W - 70, 34, 22, 0, Math.PI * 2);
  ctx.fill();
}

function drawGrass(ctx) {
  ctx.fillStyle = '#3a7a30';
  ctx.fillRect(0, HORIZON_Y, W, H - HORIZON_Y);
  ctx.fillStyle = '#2c5e24';
  ctx.fillRect(0, HORIZON_Y, W, 10);
}

function drawRoad(ctx, camX, markOffset) {
  const vpX = W / 2 + camX;

  // Surface
  ctx.fillStyle = '#5a5a5a';
  ctx.beginPath();
  ctx.moveTo(vpX - ROAD_TOP_W / 2, HORIZON_Y);
  ctx.lineTo(vpX + ROAD_TOP_W / 2, HORIZON_Y);
  ctx.lineTo(W / 2 + ROAD_BOTTOM_W / 2, H);
  ctx.lineTo(W / 2 - ROAD_BOTTOM_W / 2, H);
  ctx.closePath();
  ctx.fill();

  // White edges
  ctx.strokeStyle = '#ddd';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(vpX - ROAD_TOP_W / 2, HORIZON_Y);
  ctx.lineTo(W / 2 - ROAD_BOTTOM_W / 2, H);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(vpX + ROAD_TOP_W / 2, HORIZON_Y);
  ctx.lineTo(W / 2 + ROAD_BOTTOM_W / 2, H);
  ctx.stroke();

  // Center dashes scrolling with speed
  for (let i = 0; i < 9; i++) {
    const t1 = ((i / 9) + markOffset) % 1;
    const t2 = (((i + 0.42) / 9) + markOffset) % 1;
    if (t1 < 0.01 || t2 < 0.01 || Math.abs(t1 - t2) > 0.12) continue;
    const p1 = project(0, t1, camX);
    const p2 = project(0, t2, camX);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = lerp(0.5, 3.5, t1);
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();
  }
}

function drawTree(ctx, side, depth, camX) {
  const { x, y, scale: s } = project(side * 1.55, depth, camX);
  if (s < 0.05) return;
  // Trunk
  ctx.fillStyle = '#5c3a1e';
  ctx.fillRect(x - 4 * s, y - 50 * s, 8 * s, 32 * s);
  // Foliage layers
  ctx.fillStyle = '#2a5e2a';
  ctx.beginPath();
  ctx.arc(x, y - 50 * s, 19 * s, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#3a7a3a';
  ctx.beginPath();
  ctx.arc(x - 5 * s, y - 62 * s, 13 * s, 0, Math.PI * 2);
  ctx.fill();
}

function drawBike(ctx, x, y, scale, bodyColor, isPlayer, punching) {
  const s = scale;
  ctx.save();
  ctx.translate(x, y);

  // Shadow
  ctx.fillStyle = 'rgba(0,0,0,0.18)';
  ctx.beginPath();
  ctx.ellipse(0, 4 * s, 24 * s, 5 * s, 0, 0, Math.PI * 2);
  ctx.fill();

  // Rear wheel
  ctx.fillStyle = '#1a1a1a';
  ctx.beginPath();
  ctx.arc(-15 * s, 0, 11 * s, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#888';
  ctx.lineWidth = 2 * s;
  ctx.stroke();

  // Front wheel
  ctx.fillStyle = '#1a1a1a';
  ctx.beginPath();
  ctx.arc(15 * s, 2 * s, 9 * s, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#888';
  ctx.lineWidth = 2 * s;
  ctx.stroke();

  // Body/fairing
  ctx.fillStyle = bodyColor;
  ctx.beginPath();
  ctx.moveTo(-18 * s, -2 * s);
  ctx.lineTo(-10 * s, -15 * s);
  ctx.lineTo(8 * s, -13 * s);
  ctx.lineTo(18 * s, -3 * s);
  ctx.lineTo(18 * s, 0);
  ctx.lineTo(-18 * s, 0);
  ctx.closePath();
  ctx.fill();

  // Headlight for enemies
  if (!isPlayer) {
    ctx.fillStyle = '#ffffaa';
    ctx.beginPath();
    ctx.arc(17 * s, -6 * s, 3 * s, 0, Math.PI * 2);
    ctx.fill();
  }

  // Exhaust pipe
  ctx.strokeStyle = '#aaa';
  ctx.lineWidth = 2.5 * s;
  ctx.beginPath();
  ctx.moveTo(-18 * s, -2 * s);
  ctx.lineTo(-26 * s, 2 * s);
  ctx.stroke();

  // Rider body
  ctx.fillStyle = isPlayer ? '#8b1a00' : '#111';
  ctx.beginPath();
  rrect(ctx, -7 * s, -28 * s, 13 * s, 16 * s, 2 * s);
  ctx.fill();

  // Helmet
  ctx.fillStyle = isPlayer ? '#e85c00' : bodyColor;
  ctx.beginPath();
  ctx.arc(3 * s, -31 * s, 8 * s, 0, Math.PI * 2);
  ctx.fill();

  // Visor
  ctx.fillStyle = isPlayer ? 'rgba(255,180,80,0.85)' : 'rgba(100,180,255,0.75)';
  ctx.beginPath();
  ctx.ellipse(5 * s, -30 * s, 5 * s, 3 * s, -0.25, 0, Math.PI);
  ctx.fill();

  // Punch arm
  if (punching) {
    ctx.fillStyle = isPlayer ? '#8b1a00' : '#111';
    ctx.beginPath();
    rrect(ctx, 8 * s, -34 * s, 22 * s, 7 * s, 3 * s);
    ctx.fill();
    ctx.fillStyle = isPlayer ? '#e85c00' : '#555';
    ctx.beginPath();
    ctx.arc(32 * s, -31 * s, 7 * s, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function drawHUD(ctx, state) {
  const { speed, health, maxHealth, distance, score, taunt, tauntTimer, punchEffect } = state;

  // Speed meter
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.beginPath();
  rrect(ctx, W - 118, H - 62, 108, 52, 8);
  ctx.fill();
  ctx.fillStyle = '#999';
  ctx.font = 'bold 10px "Courier New"';
  ctx.fillText('SPEED', W - 107, H - 44);
  ctx.fillStyle = speed > 150 ? '#ff4444' : speed > 100 ? '#ffaa00' : '#66ff66';
  ctx.font = 'bold 20px "Courier New"';
  ctx.fillText(Math.round(speed) + ' km/h', W - 112, H - 20);

  // Health bar
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.beginPath();
  rrect(ctx, 8, 8, 120, 36, 6);
  ctx.fill();
  ctx.fillStyle = '#999';
  ctx.font = 'bold 10px Arial';
  ctx.fillText('HEALTH', 16, 22);
  for (let i = 0; i < maxHealth; i++) {
    ctx.fillStyle = i < health ? '#dd2222' : '#333';
    ctx.beginPath();
    rrect(ctx, 16 + i * 32, 26, 28, 12, 3);
    ctx.fill();
    if (i < health) {
      ctx.fillStyle = '#ff6666';
      ctx.beginPath();
      rrect(ctx, 17 + i * 32, 27, 10, 4, 2);
      ctx.fill();
    }
  }

  // Distance / progress
  const pct = Math.min(distance / FINISH_DIST, 1);
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.beginPath();
  rrect(ctx, W / 2 - 100, 8, 200, 36, 6);
  ctx.fill();
  ctx.fillStyle = '#999';
  ctx.font = 'bold 10px "Courier New"';
  ctx.textAlign = 'center';
  ctx.fillText(`${Math.round(distance)}m  •  SCORE: ${score}`, W / 2, 22);
  ctx.fillStyle = '#222';
  ctx.beginPath();
  rrect(ctx, W / 2 - 88, 27, 170, 10, 4);
  ctx.fill();
  ctx.fillStyle = '#33cc66';
  ctx.beginPath();
  rrect(ctx, W / 2 - 88, 27, 170 * pct, 10, 4);
  ctx.fill();
  ctx.textAlign = 'left';

  // Taunt bubble
  if (taunt && tauntTimer > 0) {
    const alpha = Math.min(1, tauntTimer / 20);
    ctx.fillStyle = `rgba(0,0,0,${0.8 * alpha})`;
    ctx.beginPath();
    rrect(ctx, W / 2 - 155, H / 2 - 22, 310, 32, 8);
    ctx.fill();
    ctx.fillStyle = `rgba(255,255,60,${alpha})`;
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(taunt, W / 2, H / 2 - 3);
    ctx.textAlign = 'left';
  }

  // Punch effect floating text
  if (punchEffect && punchEffect.timer > 0) {
    const a = punchEffect.timer / 35;
    ctx.fillStyle = `rgba(255,220,0,${a})`;
    ctx.font = `bold ${Math.round(12 + (1 - a) * 6)}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(punchEffect.text, punchEffect.x, punchEffect.y - (1 - a) * 22);
    ctx.textAlign = 'left';
  }
}

function drawControls(ctx) {
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.beginPath();
  rrect(ctx, 8, H - 60, 158, 52, 6);
  ctx.fill();
  ctx.fillStyle = '#bbb';
  ctx.font = '10px Arial';
  ctx.fillText('↑ Accelerate   ↓ Brake', 14, H - 44);
  ctx.fillText('← → Steer lane', 14, H - 30);
  ctx.fillText('SPACE — Punch a rival', 14, H - 16);
}

function drawTitleScreen(ctx, tick) {
  // Night road bg
  const g = ctx.createLinearGradient(0, 0, 0, H * 0.52);
  g.addColorStop(0, '#06061e');
  g.addColorStop(1, '#12124a');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // Road receding into horizon
  ctx.fillStyle = '#2a2a2a';
  ctx.beginPath();
  ctx.moveTo(W / 2 - 28, H * 0.52);
  ctx.lineTo(W / 2 + 28, H * 0.52);
  ctx.lineTo(W, H);
  ctx.lineTo(0, H);
  ctx.closePath();
  ctx.fill();

  // Stars (static seed)
  ctx.fillStyle = '#fff';
  for (let i = 0; i < 50; i++) {
    const sx = (i * 137.508) % W;
    const sy = (i * 79.37) % (H * 0.5);
    const brightness = 0.4 + 0.6 * Math.abs(Math.sin(tick * 0.02 + i));
    ctx.globalAlpha = brightness;
    ctx.fillRect(sx, sy, 1.5, 1.5);
  }
  ctx.globalAlpha = 1;

  // Headlights effect on road (animated)
  const pulse = 0.4 + 0.4 * Math.sin(tick * 0.08);
  const lg = ctx.createRadialGradient(W / 2, H * 0.52, 5, W / 2, H * 0.52, 180);
  lg.addColorStop(0, `rgba(255,220,100,${0.35 * pulse})`);
  lg.addColorStop(1, 'rgba(255,220,100,0)');
  ctx.fillStyle = lg;
  ctx.fillRect(0, H * 0.52, W, H - H * 0.52);

  // Title text
  ctx.save();
  ctx.textAlign = 'center';
  ctx.shadowColor = '#ff6600';
  ctx.shadowBlur = 25;
  ctx.fillStyle = '#ff6600';
  ctx.font = 'bold 54px Arial Black, Arial';
  ctx.fillText('ROAD RASH', W / 2, H / 2 - 38);
  ctx.shadowColor = '#ffcc00';
  ctx.shadowBlur = 12;
  ctx.fillStyle = '#ffcc00';
  ctx.font = 'bold 20px Arial';
  ctx.fillText('▸  A I   E D I T I O N  ◂', W / 2, H / 2 - 4);
  ctx.shadowBlur = 0;
  ctx.fillStyle = '#aaa';
  ctx.font = '13px Arial';
  ctx.fillText('Race against AI models. Punch them off the road.', W / 2, H / 2 + 26);
  ctx.fillStyle = '#555';
  ctx.font = '11px Arial';
  ctx.fillText('GPT-Raider  •  Gemini Pro  •  Copilot  •  Llama 3  •  Grok  •  DeepSeek  •  Claude 3', W / 2, H / 2 + 48);
  ctx.restore();
}

// ─── Game Logic ───────────────────────────────────────────────────────────────

function spawnEnemy(state) {
  state.enemies.push({
    rival: pick(AI_RIVALS),
    laneX: rnd(-0.65, 0.65),
    depth: rnd(0.02, 0.09),
    baseSpeed: rnd(65, 135),
    health: Math.ceil(rnd(1, 3)),
    punchCooldown: rnd(1.5, 4),
    swerveDir: 0,
    swerveTimer: 0,
    punching: false,
    punchTimer: 0,
    crashed: false,
  });
}

function updateGame(state, keys, dt) {
  // Speed
  if (keys['ArrowUp'] || keys['w']) {
    state.speed = Math.min(MAX_SPEED, state.speed + 58 * dt);
  } else if (keys['ArrowDown'] || keys['s']) {
    state.speed = Math.max(0, state.speed - 72 * dt);
  } else {
    state.speed = Math.max(0, state.speed - 14 * dt);
  }

  // Steer
  const steer = 1.5 * dt;
  if (keys['ArrowLeft'] || keys['a']) state.playerLaneX = clamp(state.playerLaneX - steer, -0.9, 0.9);
  if (keys['ArrowRight'] || keys['d']) state.playerLaneX = clamp(state.playerLaneX + steer, -0.9, 0.9);

  // Distance / win
  state.distance += state.speed * dt;
  if (state.distance >= FINISH_DIST) { state.phase = 'won'; return; }

  // Road curve (vanishing point drifts left/right)
  state.curveTimer -= dt;
  if (state.curveTimer <= 0) {
    state.curveDir = rnd(-1, 1);
    state.curveTimer = rnd(2.5, 6);
  }
  state.cameraX = clamp(state.cameraX + state.curveDir * 38 * dt, -90, 90);

  // Road mark scroll
  state.roadMarkOffset = (state.roadMarkOffset + state.speed * dt * 0.001) % 1;

  // Spawn
  state.spawnTimer -= dt;
  if (state.spawnTimer <= 0 && state.speed > 15 && state.enemies.length < 6) {
    spawnEnemy(state);
    state.spawnTimer = rnd(2, 5);
  }

  // Player punch (spacebar)
  state.punchCooldown = Math.max(0, state.punchCooldown - dt);
  state.playerPunchTimer = Math.max(0, (state.playerPunchTimer || 0) - dt);
  state.playerPunching = state.playerPunchTimer > 0;

  if ((keys[' '] || keys['Space']) && state.punchCooldown <= 0) {
    state.punchCooldown = 0.5;
    state.playerPunchTimer = 0.35;
    state.playerPunching = true;
    for (const e of state.enemies) {
      if (e.crashed) continue;
      const inRange = e.depth > 0.68 && e.depth < 0.96;
      const inLane  = Math.abs(e.laneX - state.playerLaneX) < 0.32;
      if (inRange && inLane) {
        e.health--;
        const pp = project(e.laneX - state.playerLaneX, e.depth, state.cameraX);
        state.punchEffect = { text: pick(PUNCH_TEXTS), x: pp.x, y: pp.y - 30, timer: 40 };
        if (e.health <= 0) {
          e.crashed = true;
          state.score += 150;
          state.taunt = `${e.rival.name}: ${e.rival.taunt}`;
          state.tauntTimer = 130;
        }
      }
    }
  }

  // Update enemies
  for (const e of state.enemies) {
    if (e.crashed) {
      e.depth -= 0.45 * dt; // slide behind
      continue;
    }

    // Move toward / away based on relative speed
    const relSpeed = state.speed - e.baseSpeed;
    e.depth += relSpeed * dt * 0.0022;

    // AI swerve
    e.swerveTimer -= dt;
    if (e.swerveTimer <= 0) {
      e.swerveDir = rnd(-1, 1);
      e.swerveTimer = rnd(0.8, 2.5);
    }
    e.laneX = clamp(e.laneX + e.swerveDir * 0.38 * dt, -0.82, 0.82);

    // Enemy punch player
    e.punchTimer = Math.max(0, e.punchTimer - dt);
    e.punching = e.punchTimer > 0;
    const closeDepth = e.depth > 0.72 && e.depth < 0.96;
    const sameLane   = Math.abs(e.laneX - state.playerLaneX) < 0.28;
    if (closeDepth && sameLane) {
      e.punchCooldown -= dt;
      if (e.punchCooldown <= 0) {
        e.punchCooldown = rnd(2, 4.5);
        e.punchTimer = 0.4;
        e.punching = true;
        state.health = Math.max(0, state.health - 1);
        state.taunt = `${e.rival.name} punched you! Hit SPACE back!`;
        state.tauntTimer = 110;
        if (state.health <= 0) { state.phase = 'dead'; return; }
      }
    }

    // Ram collision (reaches player)
    if (e.depth >= 0.97 && sameLane) {
      state.speed = Math.max(0, state.speed - 55);
      state.health = Math.max(0, state.health - 1);
      e.depth = 0.7;
      state.taunt = `CRASH! ${e.rival.name}: ${e.rival.taunt}`;
      state.tauntTimer = 130;
      if (state.health <= 0) { state.phase = 'dead'; return; }
    }
  }

  // Cleanup
  state.enemies = state.enemies.filter(e => e.depth > -0.6 && e.depth < 1.0);
  if (state.punchEffect) state.punchEffect.timer--;
  if (state.tauntTimer > 0) state.tauntTimer--;
}

function drawFrame(ctx, state, tick) {
  ctx.clearRect(0, 0, W, H);
  drawSky(ctx);
  drawGrass(ctx);
  drawRoad(ctx, state.cameraX, state.roadMarkOffset);

  // Collect & depth-sort all drawables
  const drawables = [];
  for (let i = 0; i < 10; i++) {
    const d = ((i / 10) + state.roadMarkOffset * 0.25) % 1;
    if (d > 0.02) {
      drawables.push({ type: 'tree', side: -1, depth: d });
      drawables.push({ type: 'tree', side: 1,  depth: d });
    }
  }
  for (const e of state.enemies) {
    if (e.depth > 0.01 && e.depth < 1.0) {
      drawables.push({ type: 'enemy', e, depth: e.depth });
    }
  }
  drawables.sort((a, b) => a.depth - b.depth);

  for (const d of drawables) {
    if (d.type === 'tree') {
      drawTree(ctx, d.side, d.depth, state.cameraX);
    } else {
      const { e } = d;
      const p = project(e.laneX - state.playerLaneX, e.depth, state.cameraX);
      drawBike(ctx, p.x, p.y, p.scale, e.rival.color, false, e.punching);
      // Name tag fades in at medium distance
      if (e.depth > 0.38 && !e.crashed) {
        const alpha = Math.min(1, (e.depth - 0.38) * 6);
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = '#fff';
        ctx.font = `bold ${Math.max(8, Math.round(9 * p.scale + 3))}px Arial`;
        ctx.textAlign = 'center';
        ctx.fillText(e.rival.name, p.x, p.y - 44 * p.scale - 2);
        ctx.restore();
      }
    }
  }

  // Player bike (always centered)
  drawBike(ctx, W / 2, PLAYER_Y, 1.0, '#cc2200', true, state.playerPunching);
  drawHUD(ctx, state);
  drawControls(ctx);
}

// ─── React Component ──────────────────────────────────────────────────────────

function RoadRash({ onClose }) {
  const canvasRef   = useRef(null);
  const stateRef    = useRef(null);
  const keysRef     = useRef({});
  const rafRef      = useRef(null);
  const lastTimeRef = useRef(null);
  const tickRef     = useRef(0);
  const [uiPhase, setUiPhase]     = useState('idle');
  const [finalScore, setFinalScore] = useState(0);

  function startGame() {
    stateRef.current = {
      phase: 'playing',
      speed: 0,
      distance: 0,
      health: 3,
      maxHealth: 3,
      score: 0,
      playerLaneX: 0,
      cameraX: 0,
      curveDir: 0,
      curveTimer: 0,
      roadMarkOffset: 0,
      enemies: [],
      spawnTimer: 3,
      taunt: null,
      tauntTimer: 0,
      punchCooldown: 0,
      punchEffect: null,
      playerPunching: false,
      playerPunchTimer: 0,
    };
    setUiPhase('playing');
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    function loop(ts) {
      if (lastTimeRef.current === null) lastTimeRef.current = ts;
      const dt = Math.min((ts - lastTimeRef.current) / 1000, 0.05);
      lastTimeRef.current = ts;
      tickRef.current++;

      const state = stateRef.current;

      if (!state) {
        drawTitleScreen(ctx, tickRef.current);
      } else {
        if (state.phase === 'playing') {
          updateGame(state, keysRef.current, dt);
          if (state.phase !== 'playing') {
            setUiPhase(state.phase);
            setFinalScore(state.score);
          }
        }
        drawFrame(ctx, state, tickRef.current);
      }

      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);

    const onKeyDown = (e) => {
      keysRef.current[e.key] = true;
      if ([' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
      }
    };
    const onKeyUp = (e) => { keysRef.current[e.key] = false; };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  return (
    <Div>
      <canvas ref={canvasRef} width={W} height={H} />

      {uiPhase === 'idle' && (
        <Overlay>
          <h2>ROAD RASH</h2>
          <div className="subtitle">⚡ AI EDITION ⚡</div>
          <p>Race against AI models. Reach the finish line.</p>
          <p>Punch them off the road before they punch you.</p>
          <p className="controls">↑↓ Speed &nbsp;|&nbsp; ←→ Steer &nbsp;|&nbsp; SPACE Punch</p>
          <button onClick={startGame}>START ENGINE</button>
        </Overlay>
      )}

      {(uiPhase === 'dead' || uiPhase === 'won') && (
        <Overlay>
          <h2 className={uiPhase === 'won' ? 'win' : 'lose'}>
            {uiPhase === 'won' ? '🏁 FINISH LINE!' : '💀 WRECKED!'}
          </h2>
          <p>
            {uiPhase === 'won'
              ? 'You beat the AI. Eat that, Gemini.'
              : 'An AI outlasted you. Embarrassing.'}
          </p>
          <div className="score">Score: {finalScore}</div>
          <button onClick={startGame}>RIDE AGAIN</button>
          <button className="secondary" onClick={onClose}>Exit</button>
        </Overlay>
      )}
    </Div>
  );
}

const Div = styled.div`
  position: relative;
  width: ${W}px;
  height: ${H}px;
  background: #000;
  overflow: hidden;
  canvas { display: block; }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  gap: 6px;

  h2 {
    font-size: 36px;
    margin: 0;
    color: #ff6600;
    text-shadow: 0 0 22px #ff6600;
  }
  h2.win  { color: #44ff88; text-shadow: 0 0 22px #44ff88; }
  h2.lose { color: #ff3333; text-shadow: 0 0 22px #ff3333; }

  .subtitle {
    font-size: 17px;
    color: #ffcc00;
    font-weight: bold;
    letter-spacing: 4px;
  }

  p {
    margin: 2px 0;
    color: #bbb;
    font-size: 13px;
  }
  .controls {
    color: #ffaa44;
    font-family: monospace;
    font-size: 12px;
    margin-top: 6px;
  }
  .score {
    font-size: 24px;
    color: #ffcc00;
    font-weight: bold;
    margin: 6px 0;
  }

  button {
    margin-top: 10px;
    padding: 10px 32px;
    font-size: 15px;
    font-weight: bold;
    background: #ff6600;
    color: #fff;
    border: none;
    cursor: pointer;
    border-radius: 4px;
    &:hover { background: #ff8833; }
  }
  button.secondary {
    background: #444;
    margin-top: 4px;
    padding: 7px 22px;
    font-size: 13px;
    &:hover { background: #555; }
  }
`;

export default RoadRash;
